import { getAnalytics, logEvent } from "firebase/analytics";
import { useSnackbar } from "notistack";
import { FC } from "react";

import { KeplrSVG } from "../../../assets";
import {
  Button,
  IOption,
  Options,
  SnackMessage,
  Text,
  WalletOption,
} from "../../../components";
import { DEFAULT_COSMOS_CHAIN_ID } from "../../../config";
import { KeplrMobile } from "../../../connectors/cosmos/KeplrMobile/KeplrMobile";
import { keplr } from "../../../connectors/cosmos/keplr";
import { useNetworks } from "../../../hooks";
import { useSupportedWalletsMap } from "../../../hooks/utils/useSupportedWalletsMap";
import { WalletItem } from "../../../interfaces";
import { shortAddress } from "../../../utils/utils";
import { useCosmJsProvider } from "../../CosmJsProvider/CosmJsProvider";
import "./ConnectWalletModal.scss";

interface Props {
  setSelectedCosmosWallet: (name: string | null) => void;
  onCloseClick: () => void;
}

export const CosmosSection: FC<Props> = ({
  setSelectedCosmosWallet,
  onCloseClick,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { connector: cosmJsConnector } = useCosmJsProvider();
  const {
    networks: { cosmos },
  } = useNetworks();
  const analytics = getAnalytics();
  const supportedWalletsMap = useSupportedWalletsMap();

  const onKeplrClick = () => {
    keplr
      .activate(DEFAULT_COSMOS_CHAIN_ID)
      .then(() => {
        localStorage.setItem(
          "lastCosmosConnector",
          supportedWalletsMap.cosmos.keplr.id,
        );
        setSelectedCosmosWallet(supportedWalletsMap.cosmos.keplr.id);
        onCloseClick();
        logEvent(analytics, "wallet_connected_success", {
          name: "keplr",
        });
      })
      .catch((err) => {
        enqueueSnackbar(
          <SnackMessage
            message={{
              text: `There was a problem while connecting to Keplr wallet. Error: ${
                err?.message || "unknown"
              }`,
              variant: "error",
            }}
          />,
        );
        onCloseClick();
        logEvent(analytics, "wallet_connected_fail", {
          name: "keplr",
        });
      });
  };

  const onKeplrMobileClick = () => {
    KeplrMobile.activate(DEFAULT_COSMOS_CHAIN_ID)
      .then(() => {
        localStorage.setItem(
          "lastCosmosConnector",
          supportedWalletsMap.cosmos.keplrMobile.id,
        );
        setSelectedCosmosWallet(supportedWalletsMap.cosmos.keplrMobile.id);
        onCloseClick();
        logEvent(analytics, "wallet_connected_success", {
          name: "keplr_mobile",
        });
      })
      .catch((err) => {
        enqueueSnackbar(
          <SnackMessage
            message={{
              text: `There was a problem while connecting to Keplr Mobile wallet. Error: ${
                err?.message || "unknown"
              }`,
              variant: "error",
            }}
          />,
        );
        onCloseClick();
        logEvent(analytics, "wallet_connected_fail", {
          name: "keplr_mobile",
        });
      });
  };

  const cosmosWallets: Record<string, WalletItem> = {
    Keplr: {
      name: "Keplr",
      logo: (
        <KeplrSVG
          className="connect-wallet-modal__svg"
          width={24}
          height={24}
        />
      ),
      onClick: onKeplrClick,
      address: cosmos.address,
      disabled: false,
    },
    "Keplr Mobile": {
      name: "Keplr Mobile",
      logo: (
        <KeplrSVG
          className="connect-wallet-modal__svg"
          width={24}
          height={24}
        />
      ),
      onClick: onKeplrMobileClick,
      address: cosmos.address,
      disabled: false,
    },
  };

  const cosmosWalletOptions: IOption[] = Object.values(cosmosWallets)
    .filter((wallet) => !wallet.address)
    .map((wallet) => {
      return {
        id: wallet.name,
        content: <WalletOption wallet={wallet} />,
      };
    });

  const disconnectCosmosWallet = async () => {
    localStorage.removeItem("lastCosmosConnector");
    await cosmJsConnector.deactivate();

    setSelectedCosmosWallet(null);
  };

  return cosmos.address ? (
    <div className="connect-wallet-modal__connection-info">
      <div className="connect-wallet-modal__connection-info-wrapper">
        <p className="connect-wallet-modal__connection-info-title">Cosmos</p>
        <Text
          text={`Connected Wallet: <span>${shortAddress(
            cosmos.address,
          )}</span> `}
        />
      </div>
      <div>
        <Button onClick={disconnectCosmosWallet} variant="OUTLINED">
          Disconnect
        </Button>
      </div>
    </div>
  ) : (
    <div className="connect-wallet-modal__connection-info-options">
      <p className="connect-wallet-modal__connection-info-title">Cosmos</p>
      <Options
        id="COSMOS"
        handleChange={(name) => cosmosWallets[name].onClick()}
        options={cosmosWalletOptions}
      />
    </div>
  );
};
